import React, { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useI18n, formatCurrency } from '@/lib/i18n';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, Utensils } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function Menu() {
  const { t, lang } = useI18n();
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [catDialog, setCatDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [c, m, r] = await Promise.all([
      api.get('/menu-categories'),
      api.get('/menu-items'),
      api.get('/recipes'),
    ]);
    setCategories(c.data);
    setItems(m.data);
    setRecipes(r.data);
    setActiveCat(prev => prev || c.data[0]?.id || null);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filteredItems = activeCat ? items.filter(i => i.categoria_id === activeCat) : items;

  return (
    <div className="space-y-4" data-testid="menu-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold">{t('nav_menu')}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCatDialog(true)} data-testid="menu-new-category-button"><Plus className="h-4 w-4 mr-1" /> {t('new_category')}</Button>
          <Button onClick={() => { setEditing(null); setItemDialog(true); }} data-testid="menu-new-dish-button"><Plus className="h-4 w-4 mr-1" /> {t('new_dish')}</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
        <Card className="h-fit">
          <CardHeader><CardTitle className="text-sm uppercase tracking-wider text-[hsl(var(--muted-foreground))]">{t('menu_categories')}</CardTitle></CardHeader>
          <CardContent className="p-2">
            {categories.length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))] p-3">{lang === 'es' ? 'Sin categorías' : 'Sem categorias'}</p>
            ) : categories.map(c => (
              <button
                key={c.id}
                onClick={() => setActiveCat(c.id)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-lg text-sm font-medium mb-1',
                  activeCat === c.id ? 'bg-[hsl(var(--accent))]' : 'hover:bg-[hsl(var(--muted))]'
                )}
                data-testid={`menu-category-${c.id}`}
              >
                {c.nombre}
              </button>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-3">
            {filteredItems.length === 0 ? (
              <div className="text-center py-12">
                <Utensils className="h-10 w-10 text-[hsl(var(--muted-foreground))] mx-auto mb-3" />
                <p className="text-sm text-[hsl(var(--muted-foreground))]">
                  {lang === 'es' ? 'Añade platos a esta categoría' : 'Adicione pratos a esta categoria'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {filteredItems.map(it => (
                  <Card key={it.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setEditing(it); setItemDialog(true); }} data-testid={`menu-item-card-${it.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-heading font-semibold truncate">{it.nombre}</h3>
                          {it.descripcion && <p className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2">{it.descripcion}</p>}
                        </div>
                        <Badge variant={it.activo ? 'default' : 'outline'}>
                          {it.activo ? t('active') : t('no')}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <Badge variant="outline" className="text-xs">IVA {it.iva_pct}%</Badge>
                        <div className="font-mono font-bold">{formatCurrency(it.pvp, lang)}</div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CategoryDialog open={catDialog} onOpenChange={setCatDialog} t={t} onCreated={load} />
      <ItemDialog open={itemDialog} onOpenChange={setItemDialog} t={t} lang={lang} categories={categories} recipes={recipes} editing={editing} onSaved={load} />
    </div>
  );
}

function CategoryDialog({ open, onOpenChange, t, onCreated }) {
  const [form, setForm] = useState({ nombre: '', orden: 0 });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="menu-category-dialog">
        <DialogHeader><DialogTitle>{t('new_category')}</DialogTitle></DialogHeader>
        <div><Label>{t('name')}</Label><Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="menu-category-name-input" /></div>
        <DialogFooter>
          <Button onClick={async () => {
            await api.post('/menu-categories', form);
            onOpenChange(false); setForm({ nombre: '', orden: 0 }); onCreated();
            toast.success('OK');
          }} disabled={!form.nombre} data-testid="menu-category-save-button">{t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemDialog({ open, onOpenChange, t, lang, categories, recipes, editing, onSaved }) {
  const [form, setForm] = useState({
    nombre: '', descripcion: '', categoria_id: '', recipe_id: '',
    pvp: 0, iva_pct: 10, activo: true, alergenos: [], imagen_url: '',
  });

  useEffect(() => {
    if (editing) {
      setForm({
        nombre: editing.nombre || '', descripcion: editing.descripcion || '',
        categoria_id: editing.categoria_id || '', recipe_id: editing.recipe_id || '',
        pvp: editing.pvp || 0, iva_pct: editing.iva_pct || 10,
        activo: editing.activo !== false, alergenos: editing.alergenos || [],
        imagen_url: editing.imagen_url || '',
      });
    } else {
      setForm({ nombre: '', descripcion: '', categoria_id: categories[0]?.id || '', recipe_id: '', pvp: 0, iva_pct: 10, activo: true, alergenos: [], imagen_url: '' });
    }
  }, [editing, categories]);

  const save = async () => {
    const payload = { ...form, recipe_id: form.recipe_id || null };
    if (editing) await api.put(`/menu-items/${editing.id}`, payload);
    else await api.post('/menu-items', payload);
    toast.success('OK');
    onOpenChange(false); onSaved();
  };
  const remove = async () => {
    await api.delete(`/menu-items/${editing.id}`);
    onOpenChange(false); onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="menu-item-dialog">
        <DialogHeader><DialogTitle>{editing ? t('edit') : t('new_dish')}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>{t('name')}</Label><Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} data-testid="menu-item-name-input" /></div>
          <div><Label>{t('description')}</Label><Input value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t('category')}</Label>
              <Select value={form.categoria_id} onValueChange={v => setForm({ ...form, categoria_id: v })}>
                <SelectTrigger data-testid="menu-item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('link_recipe')}</Label>
              <Select value={form.recipe_id || 'none'} onValueChange={v => setForm({ ...form, recipe_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="-" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-</SelectItem>
                  {recipes.map(r => <SelectItem key={r.id} value={r.id}>{r.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t('price')} (con IVA)</Label><Input type="number" step="0.01" value={form.pvp} onChange={e => setForm({ ...form, pvp: Number(e.target.value) })} data-testid="menu-item-price-input" /></div>
            <div>
              <Label>{t('iva_pct')}</Label>
              <Select value={String(form.iva_pct)} onValueChange={v => setForm({ ...form, iva_pct: Number(v) })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="4">4% (reducido)</SelectItem>
                  <SelectItem value="10">10% (hostelería)</SelectItem>
                  <SelectItem value="21">21% (general/alcohol)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label>{t('active')}</Label>
            <Switch checked={form.activo} onCheckedChange={v => setForm({ ...form, activo: v })} data-testid="menu-item-active-switch" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          {editing && <Button variant="destructive" onClick={remove} data-testid="menu-item-delete-button"><Trash2 className="h-4 w-4" /></Button>}
          <Button onClick={save} disabled={!form.nombre || !form.categoria_id || !form.pvp} data-testid="menu-item-save-button">{t('save')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
